import {
  baseCdcEventDataSchema,
  cdcAuthContextSchema,
  cdcEventModule,
  cdcEventType,
  cdcFullUpdateFields,
} from '@tempo-io/common-dto';
import { discriminatedUnion, z } from 'zod';

export const workIntegrationBaseCdcEventSchema = baseCdcEventDataSchema.extend({
  module: z.literal(cdcEventModule.enum['work-integration']),
});

export const workIntegrationStatus = z.enum([
  'CREATED',
  'QUEUED',
  'RUNNING',
  'COMPLETED',
  'COMPLETED_WITH_PENDING',
  'PARTIAL_FAILURE',
  'FAILED',
]);

export const synchronizationStatusSchema = z.enum(['ENABLED', 'DISABLED', 'DELETING']);

export const sourceSystemTypeSchema = z.enum(['JIRA', 'ADO']);

export const workIntegrationIdentifierSchema = z.object({
  id: z.number().int(),
});

// The Kotlin producer serializes its whole domain object with no dedicated DTO, so a new
// column shows up as a new JSON field with no warning (and no coordinated release) on either
// side. `.loose()` means an unreviewed field parses without error instead of turning a routine
// Kotlin change into an audit-trail outage; the untyped fields below are declared only so
// `auditedWorkIntegrationFields` (derived from this shape) has a single, reviewed record of
// what's known-safe to persist, without asserting a type we can't verify from this side.
export const workIntegrationEntitySchema = z
  .object({
    id: z.number().int(),
    organizationId: z.unknown().optional(),
    integrationInstanceId: z.unknown().optional(),
    loopId: z.unknown().optional(),
    credentialsId: z.unknown().optional(),
    projectId: z.unknown().optional(),
    instanceId: z.unknown().optional(),
    instanceUrl: z.unknown().optional(),
    name: z.unknown().optional(),
    sourceSystemType: sourceSystemTypeSchema.optional(),
    status: workIntegrationStatus,
    synchronizationStatus: synchronizationStatusSchema.optional(),
    fieldMapping: z.unknown().optional(),
    filters: z.unknown().optional(),
    lastPullSyncAt: z.string().nullish(),
    lastPushSyncAt: z.string().nullish(),
    lastPulledBy: z.string().nullish(),
    lastPushedBy: z.string().nullish(),
    isLastPullFullSync: z.unknown().optional(),
    isLastPushFullSync: z.unknown().optional(),
    fieldMappingsUpdatedAt: z.unknown().optional(),
    createdBy: z.unknown().optional(),
    updatedBy: z.unknown().optional(),
    sourceOrgId: z.unknown().optional(),
    createdAt: z.unknown().optional(),
    updatedAt: z.unknown().optional(),
    deletionRetryCount: z.unknown().optional(),
    skipDescription: z.unknown().optional(),
    pushRankSyncEnabled: z.unknown().optional(),
    excludedTeamCount: z.number().int().optional(),
    excludedTeams: z.array(z.object({ teamName: z.string(), loopName: z.string() })).optional(),
    displayName: z.unknown().optional(),
    hasMappingChangeSinceLastPull: z.unknown().optional(),
  })
  .loose();

export const auditedWorkIntegrationFields: ReadonlySet<string> = new Set(
  Object.keys(workIntegrationEntitySchema.shape),
);

export const workIntegrationDataChangeDTOSchema = discriminatedUnion('eventType', [
  workIntegrationBaseCdcEventSchema.extend({
    entityType: z.literal('work-integration'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: workIntegrationEntitySchema,
  }),
  workIntegrationBaseCdcEventSchema.extend({
    entityType: z.literal('work-integration'),
    ...cdcFullUpdateFields(workIntegrationEntitySchema),
  }),
  workIntegrationBaseCdcEventSchema.extend({
    entityType: z.literal('work-integration'),
    eventType: z.literal(cdcEventType.enum.deleted),
    entity: workIntegrationIdentifierSchema,
  }),
]);

// Deliberately excludes the refresh token itself - this is the whole payload OAuthCredentialAuditPayload
// (Kotlin) publishes, never the OAuthCredential domain object, so it needs no allow-list of its own.
export const oAuthCredentialAuditPayloadSchema = z.object({
  id: z.number().int(),
  organizationId: z.string(),
  userId: z.string(),
  sourceId: z.string(),
  sourceSystemType: sourceSystemTypeSchema,
});

export const oauthCredentialDataChangeDTOSchema = discriminatedUnion('eventType', [
  workIntegrationBaseCdcEventSchema.extend({
    entityType: z.literal('oauth-credential'),
    eventType: z.literal(cdcEventType.enum.created),
    entity: oAuthCredentialAuditPayloadSchema,
  }),
  workIntegrationBaseCdcEventSchema.extend({
    entityType: z.literal('oauth-credential'),
    ...cdcFullUpdateFields(oAuthCredentialAuditPayloadSchema),
  }),
]);

// The work-integration CDC topic carries more than one entity type (WorkIntegrationEntityType in
// Kotlin) - each gets its own eventType-discriminated schema above, composed here by entityType so
// every consumer of this topic (audit, notification) recognizes all of them, not just the original one.
export const workIntegrationServiceDataChangeDTOSchema = z.object({
  data: workIntegrationBaseCdcEventSchema.and(
    discriminatedUnion('entityType', [
      workIntegrationDataChangeDTOSchema,
      oauthCredentialDataChangeDTOSchema,
    ]),
  ),
  authContext: cdcAuthContextSchema,
  metadata: z.record(z.string(), z.string()).optional(),
});

export type WorkIntegrationServiceDataChangeDTO = z.output<
  typeof workIntegrationServiceDataChangeDTOSchema
>;
export type WorkIntegrationDataChangeDTO = WorkIntegrationServiceDataChangeDTO['data'];
